import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FileText, 
  DollarSign, 
  Percent, 
  Calendar, 
  TrendingUp,
  Search 
} from 'lucide-react';
import axios from 'axios';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { formatCurrency, formatDate, getStatusColor } from '@/lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LenderPortfolio() {
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchLoans = async () => {
      try {
        const res = await axios.get(`${API}/loans`);
        setLoans(res.data);
      } catch (err) {
        console.error('Failed to fetch loans:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchLoans();
  }, []);

  const totalLent = loans.reduce((sum, l) => sum + l.amount, 0);
  const totalEarnings = loans.reduce((sum, l) => sum + (l.total_repayment - l.amount), 0);
  const avgRate = loans.length ? loans.reduce((sum, l) => sum + l.interest_rate, 0) / loans.length : 0;

  const filteredLoans = loans.filter(l => 
    l.amount.toString().includes(search) ||
    l.status.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <DashboardLayout title="Portfolio">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Portfolio">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="glass border-white/10">
          <CardContent className="p-6">
            <DollarSign className="w-8 h-8 text-green-500 mb-2" />
            <div className="text-2xl font-bold">{formatCurrency(totalLent)}</div>
            <div className="text-sm text-muted-foreground">Total Lent</div>
          </CardContent>
        </Card>
        <Card className="glass border-white/10">
          <CardContent className="p-6">
            <TrendingUp className="w-8 h-8 text-purple-500 mb-2" />
            <div className="text-2xl font-bold">{formatCurrency(totalEarnings)}</div>
            <div className="text-sm text-muted-foreground">Total Earnings</div>
          </CardContent>
        </Card>
        <Card className="glass border-white/10">
          <CardContent className="p-6">
            <Percent className="w-8 h-8 text-blue-500 mb-2" />
            <div className="text-2xl font-bold">{avgRate.toFixed(1)}%</div>
            <div className="text-sm text-muted-foreground">Average Rate</div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="flex flex-col md:flex-row justify-between gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            data-testid="search-portfolio-input"
            placeholder="Search loans..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-white/5 border-white/10"
          />
        </div>
      </div>

      {/* Loans List */}
      {filteredLoans.length === 0 ? (
        <Card className="glass border-white/10">
          <CardContent className="py-12 text-center">
            <FileText className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-xl font-semibold mb-2">No Active Loans</h3>
            <p className="text-muted-foreground">
              Your funded loans will appear here.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredLoans.map((loan, i) => (
            <motion.div
              key={loan.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
            >
              <Card className="glass border-white/10">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-2xl font-bold">{formatCurrency(loan.amount)}</h3>
                        <Badge className={getStatusColor(loan.status)}>
                          {loan.status}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">Rate:</span>
                          <span className="ml-2 font-medium">{loan.interest_rate}%</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Term:</span>
                          <span className="ml-2 font-medium">{loan.term_months}mo</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Monthly:</span>
                          <span className="ml-2 font-medium">{formatCurrency(loan.monthly_payment)}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Total Return:</span>
                          <span className="ml-2 font-medium text-green-500">{formatCurrency(loan.total_repayment)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right text-sm">
                      <div className="text-muted-foreground">Funded</div>
                      <div>{formatDate(loan.funded_at)}</div>
                      <div className="text-muted-foreground mt-2">Due</div>
                      <div>{formatDate(loan.due_date)}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}
